import type { NetworkCampaign } from '@common/types/campaign';
import { CampaignSection } from '@common/components/CampaignSection';
import { toCampaignListErrorMessage } from '@common/constants/campaignErrors';
import { CustomerCampaignSectionProps } from '@campaign/types/campaign';
import { Box, Typography } from '@mui/material';

/**
 * CustomerCampaignSection
 *
 * Renders the customer campaign section
 * @param campaignList - the list of campaigns
 * @param userCampaignList - the list of user campaigns
 * @param userIsLoggedIn - whether the user is logged in
 * @param selectedCampaignTab - the selected campaign tab
 * @param onSelectedCampaignTabChange - the function to change the selected campaign tab
 * @param campaignListPagination - the pagination for the campaign list
 * @param userCampaignListPagination - the pagination for the user campaign list
 * @param onChangeCampaignListPage - the function to change the campaign list page
 * @param onChangeUserCampaignListPage - the function to change the user campaign list page
 * @param isCampaignListPaginationLoading - whether the campaign list pagination is loading
 * @param isUserCampaignListPaginationLoading - whether the user campaign list pagination is loading
 * @param campaignListError - the error for the campaign list
 * @param userCampaignListError - the error for the user campaign list
 * @returns the customer campaign section
 */
export function CustomerCampaignSection({
  campaignList,
  userCampaignList,
  userIsLoggedIn,
  selectedCampaignTab,
  onSelectedCampaignTabChange,
  campaignListPagination,
  userCampaignListPagination,
  onChangeCampaignListPage,
  onChangeUserCampaignListPage,
  isCampaignListPaginationLoading,
  isUserCampaignListPaginationLoading,
  campaignListError = '',
  userCampaignListError = '',
}: CustomerCampaignSectionProps) {
  const displayedSelectedTab = userIsLoggedIn ? selectedCampaignTab : 0;

  return (
    <CampaignSection
      selectedTab={displayedSelectedTab}
      onSelectedTabChange={onSelectedCampaignTabChange}
      titleBlock={
        <Typography
          sx={(theme) => ({
            fontSize: '34px',
            fontWeight: 700,
            marginBottom: '30px',
            [theme.breakpoints.down('md')]: {
              fontSize: '22px',
              fontWeight: 600,
              marginBottom: '20px',
            },
          })}
        >
          明治会員ID限定キャンペーン
        </Typography>
      }
      renderListHeader={({ isUserCampaignTab }) =>
        isUserCampaignTab ? (
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              gap: '32px',
              marginBottom: '64px',
              [theme.breakpoints.down('md')]: {
                marginBottom: '32px',
              },
            })}
          >
            <Typography
              variant="xxxl"
              sx={(theme) => ({
                fontSize: '32px',
                [theme.breakpoints.down('md')]: {
                  fontSize: '20px',
                },
              })}
            >
              エントリー済キャンペーン一覧
            </Typography>
          </Box>
        ) : (
          <Box
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'column',
              gap: '32px',
              marginBottom: '64px',
              [theme.breakpoints.down('md')]: {
                marginBottom: '32px',
              },
            })}
          >
            <Typography
              variant="xxxl"
              sx={(theme) => ({
                fontSize: '32px',
                [theme.breakpoints.down('md')]: {
                  fontSize: '20px',
                },
              })}
            >
              キャンペーン一覧
            </Typography>
            <Typography
              sx={(theme) => ({
                fontSize: '18px',
                [theme.breakpoints.down('md')]: {
                  fontSize: '15px',
                },
              })}
            >
              {userIsLoggedIn
                ? 'エントリーボタンを押すだけで簡単に参加できます'
                : 'ログインするとエントリーボタンを押すだけで簡単に参加できます'}
            </Typography>
          </Box>
        )
      }
      getCampaignHref={(campaign: NetworkCampaign) =>
        `/campaignDetail/${campaign.organizationId}/${campaign.campaignId}`
      }
      userIsLoggedIn={userIsLoggedIn}
      campaignList={campaignList}
      userCampaignList={userCampaignList}
      campaignListPagination={campaignListPagination}
      userCampaignListPagination={userCampaignListPagination}
      onChangeCampaignListPage={onChangeCampaignListPage}
      onChangeUserCampaignListPage={onChangeUserCampaignListPage}
      isCampaignListPaginationLoading={isCampaignListPaginationLoading}
      isUserCampaignListPaginationLoading={isUserCampaignListPaginationLoading}
      campaignListError={toCampaignListErrorMessage(campaignListError)}
      userCampaignListError={toCampaignListErrorMessage(userCampaignListError)}
    />
  );
}
